import React, { useCallback, useEffect, useMemo } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Lottie from "lottie-react";
import { ChallengeInvitationList } from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./FindFriendsPage.scss";
import FixedFooter from "../../../components/FixedFooter/FixedFooter";
import { OnboardingSteps } from "../../../utils/constants";
import FilledButton, {
  ButtonSizes,
} from "../../../components/dashboard-page/FilledButton/FilledButton";
import ChatAnimation from "../../../assets/animations/friends-chat.json";
import useOnboardingSteps from "../../../hooks/useOnboardingSteps";
import ImageX from "../../../images/x-outlined-white.svg";
import PrimaryActionButton from "../../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import { POST_TO_X_LINK } from "../../../utils/constants/links";
import useGetMyChallengeInvitationsQuery from "../../../react-query/queries/use-get-my-challenge-invitations-query";
import useCreateChallengeInvitationMutation from "../../../react-query/mutations/use-create-challenge-invitation-mutation";
import useMyChallengeInvitationDefaultGameQuery from "../../../react-query/queries/use-my-challenge-invitations-default-game-query";
import { FindFriendsOptionProperty } from "../../../utils/analytics";
import useAnalytics from "../../../hooks/useAnalytics";

function FindFriendsPage() {
  const { t } = useTranslation();
  const { goToStep } = useOnboardingSteps();
  const { invitationText, challengeInvitation } = useChallengeOnX();
  const analytics = useAnalytics();

  const handleClick = useCallback(
    (findFriendsOption: FindFriendsOptionProperty) => {
      analytics.sendFindFriendOptionsEvent(findFriendsOption);
      goToStep(OnboardingSteps.GET_APP);
    },
    [analytics, goToStep]
  );

  return (
    <Container className="MobileOnly position-relative FindFriendsPage px-3">
      <Col>
        <h1>{t("find_friends_playing_hyper")}.</h1>
        <p className="subhead">
          {t("play_battle_games_with_friends_and_see_proof_of_their_earnings")}.
        </p>
      </Col>
      <Col>
        <Lottie animationData={ChatAnimation} loop={true} />
      </Col>
      <FixedFooter>
        <Container className="d-flex justify-content-center goButtonContainer mt-0">
          <PrimaryActionButton
            as="a"
            href={`${POST_TO_X_LINK}?text=${invitationText}`}
            target="_blank"
            rel="noreferrer"
            text={t("find_friends")}
            onClick={() => handleClick(FindFriendsOptionProperty.FIND_FRIENDS)}
            leftImage={ImageX}
            style={{
              height: 56,
              borderRadius: 12,
              paddingTop: 8,
            }}
            disabled={!challengeInvitation}
          />
          <FilledButton
            size={ButtonSizes.MD}
            onClick={() => handleClick(FindFriendsOptionProperty.SKIP)}
            style={{ background: "#191040", marginTop: 20 }}
          >
            {t("remind_me_later")}
          </FilledButton>
        </Container>
      </FixedFooter>
    </Container>
  );
}

export default FindFriendsPage;

export function useChallengeOnX() {
  const { t } = useTranslation();
  const { challengeInvitations } = useGetMyChallengeInvitationsQuery({
    limit: 20,
  });
  const { gameId } = useMyChallengeInvitationDefaultGameQuery();
  const challengeInvitation = useChallengeInvitation(
    challengeInvitations,
    gameId
  );
  const gameName = challengeInvitation?.games?.[0]?.name;
  const invitationText = `${t("play_game_with_me_on_hyper", {
    gameName,
  })}. ${t("lets_go")}! ${challengeInvitation?.url}`;

  return { invitationText, challengeInvitation };
}

function findZeroStakeChallengeBelowRequestLimit(
  invitations?: ChallengeInvitationList
) {
  if (!invitations || !invitations.records?.length) return;

  return invitations.records.find(
    (invitation) =>
      invitation.stakeAmount === 0 &&
      (invitation.requestCount || 0) < invitation.requestLimit
  );
}

function useChallengeInvitation(
  invitations?: ChallengeInvitationList,
  gameId?: string
) {
  const { mutate, data: createdChallengeInvitation } =
    useCreateChallengeInvitationMutation();

  const foundChallengeInvitation = useMemo(
    () => findZeroStakeChallengeBelowRequestLimit(invitations),
    [invitations]
  );

  useEffect(() => {
    if (!invitations || foundChallengeInvitation || !gameId) return;

    mutate({
      requestLimit: 100,
      stakeAmount: 0,
      gameIds: [gameId],
    });
  }, [foundChallengeInvitation, gameId, invitations, mutate]);

  return foundChallengeInvitation || createdChallengeInvitation;
}
